import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import JsBarcode from "jsbarcode";
import { memo, useEffect, useRef, useState } from "react";
import {
  downloadBarcodePng,
  type BarcodeRenderOptions,
} from "./barcode-generator-utils";

/** Props for a single rendered barcode cell. */
export type BarcodePreviewProps = {
  /** The value to encode. */
  readonly value: string;
  /** JsBarcode render options; must be a stable reference to keep the memo effective. */
  readonly options: BarcodeRenderOptions;
};

const styles = {
  cell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    p: 1,
    position: "relative",
    minHeight: 80,
    "&:hover .barcode-download-btn": { opacity: 1 },
  },
  downloadButton: {
    position: "absolute",
    top: 4,
    right: 4,
    opacity: 0,
    transition: "opacity 0.15s",
  },
  error: {
    color: "error.main",
    fontSize: 12,
    wordBreak: "break-all",
    textAlign: "center",
  },
  svg: { maxWidth: "100%" },
  svgHidden: { display: "none" },
} satisfies Record<string, SxProps<Theme>>;

/**
 * Renders a single barcode as inline SVG with a hover-to-download PNG action.
 *
 * Invalid values for the chosen symbology (JsBarcode throws) are caught and
 * surfaced as an inline error message rather than crashing the grid. Memoized
 * so unrelated grid updates do not re-render every cell.
 *
 * @param props - The value and presentation options for this barcode.
 */
const BarcodePreviewComponent = ({ value, options }: BarcodePreviewProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!svgRef.current) {
      return;
    }
    try {
      JsBarcode(svgRef.current, value, options);
      setError(null);
    } catch (renderError) {
      setError(
        renderError instanceof Error
          ? renderError.message
          : `Cannot encode "${value}"`,
      );
    }
  }, [value, options]);

  /**
   * Renders the barcode to an off-screen canvas and triggers a PNG download.
   */
  const handleDownload = () => {
    try {
      downloadBarcodePng(value, options);
    } catch {
      // Invalid value already surfaced in the preview; nothing to download.
    }
  };

  return (
    <Paper variant="outlined" sx={styles.cell}>
      {error === null && (
        <Tooltip title="Download PNG">
          <IconButton
            className="barcode-download-btn no-print"
            size="small"
            sx={styles.downloadButton}
            onClick={handleDownload}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Box
        ref={svgRef}
        component="svg"
        sx={error === null ? styles.svg : styles.svgHidden}
      />
      {error !== null && <Typography sx={styles.error}>{error}</Typography>}
    </Paper>
  );
};

BarcodePreviewComponent.displayName = "BarcodePreview";

/** Memoized single-barcode preview cell. */
export const BarcodePreview = memo(BarcodePreviewComponent);
